/**
 * The Tasks page's inline cell edits — status, due date and reassign.
 *
 * **Registry, not special cases** (CLAUDE.md rule 6). None of the three knows what a status or
 * a date is: the row's board mapping names a *column*, `boards.read` says what **type** that
 * column is, and `getColumnUi(type).Editor` is the same component the board grid opens. A board
 * whose due column is a `timeline` therefore gets monday's timeline editor here, and a board
 * that maps a `dropdown` as its assignee column would get the dropdown picker — without a line
 * of `if (type === …)` anywhere in this folder.
 *
 * The write goes through `cells.set` / `cells.bulkSet`, i.e. through `services/cells.ts`, which
 * is what makes the edit indistinguishable from the same edit made on the board: the same
 * permission check, the same activity row, the same outbox event, and the same
 * `syncItemTasks` (`cells.ts` step 7b) that puts the row back on this page.
 */
import { useEffect, useState, type ComponentType, type ReactNode } from 'react';
import type { ColumnAggregation, ColumnId, GroupId, ItemId, UserId } from '@elizmonday/shared';
import { getColumnUi, normaliseColumnSettings } from '../board-view/columns/registry';
import { browserTimezone } from '../board-view/columns/context';
import type { BoardColumn, ItemRow, Person } from '../board-view/data/types';
import type { CellEditorProps, CellRenderContext } from '../board-view/columns/types';
import type { TaskColumnView, TaskItemContext, TaskRowView, TasksSource } from './types';

/** Which role of the board's task mapping an editor is opening. */
export type TaskCellRole = 'status' | 'due' | 'assignee';

export interface TaskEditorTarget {
  row: TaskRowView;
  role: TaskCellRole;
}

/** A `BoardColumn` for one `TaskColumnView`, so the registry's editor gets what it expects. */
export function toBoardColumn(column: TaskColumnView, position: number): BoardColumn {
  return {
    id: column.id as ColumnId,
    type: column.type,
    title: column.title,
    description: null,
    settings: normaliseColumnSettings(column.type, column.settingsStr ?? {}),
    width: column.width,
    position,
    revision: column.revision,
    aggregation: 'none' as ColumnAggregation,
  };
}

/** The synthetic grid row the editor is opened on. It carries the item's real cell values. */
export function toItemRow(row: TaskRowView, values: Record<string, unknown>): ItemRow {
  return {
    id: row.itemId as ItemId,
    name: row.itemName,
    groupId: '' as GroupId,
    position: 0,
    updatesCount: 0,
    subitemIds: [],
    values,
  };
}

export function toRenderContext(context: TaskItemContext, columns: BoardColumn[]): CellRenderContext {
  const people: Person[] = context.people.map((person) => ({
    id: person.id as UserId,
    name: person.name ?? '',
    email: null,
    photoUrl: person.photoUrl,
    // The avatar tint is derived from the id everywhere else; the picker only needs a name.
    color: 'explosive',
  }));
  return { people, columns, search: '', timezone: browserTimezone() };
}

/** The mapped column for a role, or `null` when the board maps none. */
export function columnForRole(
  context: TaskItemContext,
  mapping: { statusColumnId: string | null; dueColumnId: string | null; assigneeColumnIds: string[] },
  role: TaskCellRole,
): TaskColumnView | null {
  const id =
    role === 'status'
      ? mapping.statusColumnId
      : role === 'due'
        ? mapping.dueColumnId
        : (mapping.assigneeColumnIds[0] ?? null);
  if (id === null) return null;
  return context.columns.find((column) => column.id === id) ?? null;
}

export interface TaskCellEditorProps {
  source: TasksSource;
  target: TaskEditorTarget;
  /** The row's board mapping, already loaded by the page. */
  mapping: { statusColumnId: string | null; dueColumnId: string | null; assigneeColumnIds: string[] };
  /** Called after a successful write, so the page can invalidate the list. */
  onWritten(): void;
  onClose(): void;
  /** Rendered instead of the editor when the item's cells could not be read. */
  renderUnavailable(reason: string): ReactNode;
}

/**
 * Opens the mapped column's own editor over the row.
 *
 * It is mounted only while an editor is open, so the `boards.read` behind `itemContext` is paid
 * once per edit and never on a page that is merely being scrolled.
 */
export function TaskCellEditor({
  source,
  target,
  mapping,
  onWritten,
  onClose,
  renderUnavailable,
}: TaskCellEditorProps) {
  const [context, setContext] = useState<TaskItemContext | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    source
      .itemContext({
        boardId: target.row.boardId,
        itemId: target.row.itemId,
        itemName: target.row.itemName,
      })
      .then((next) => {
        if (!cancelled) setContext(next);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : 'The item could not be read');
      });
    return () => {
      cancelled = true;
    };
  }, [source, target.row.boardId, target.row.itemId, target.row.itemName]);

  if (error !== null) return <>{renderUnavailable(error)}</>;
  if (context === null) {
    return (
      <span className="ez-tasks__editor-loading" role="status" data-testid="tasks-editor-loading">
        Loading…
      </span>
    );
  }
  if (!context.found) {
    return <>{renderUnavailable('This item has moved or is no longer visible. Open it to edit it.')}</>;
  }

  const column = columnForRole(context, mapping, target.role);
  if (column === null) {
    return <>{renderUnavailable('This board has no column mapped for that.')}</>;
  }
  const ui = getColumnUi(column.type);
  if (!ui.Editor) {
    return <>{renderUnavailable(`A ${column.type} column cannot be edited from here yet.`)}</>;
  }

  const columns = context.columns.map((entry, index) => toBoardColumn(entry, index));
  const boardColumn = columns.find((entry) => entry.id === column.id) ?? toBoardColumn(column, 0);
  const item = toItemRow(target.row, context.values);
  const renderContext = toRenderContext(context, columns);
  /*
   * The registry is heterogeneous by design — every type's editor is typed against its own value
   * and settings — so the host takes the one shape they all share (`CellEditorProps<unknown,
   * unknown>`). The values handed in are the column's own, read from the item a moment ago.
   */
  const Editor = ui.Editor as unknown as ComponentType<CellEditorProps<unknown, unknown>>;

  return (
    <span className="ez-tasks__editor" data-testid={`tasks-editor-${target.role}`}>
      <Editor
        value={context.values[column.id] ?? null}
        settings={boardColumn.settings}
        column={boardColumn}
        item={item}
        context={renderContext}
        readOnly={false}
        autoFocus
        onCommit={(next: unknown) => {
          void source
            .setCell({
              boardId: target.row.boardId,
              itemId: target.row.itemId,
              columnId: column.id,
              value: next,
            })
            .then(() => {
              onWritten();
              onClose();
            })
            .catch(() => onClose());
        }}
        onCancel={onClose}
        onNavigate={() => undefined}
      />
    </span>
  );
}
